import { CheckmarkFilled, ErrorFilled } from '@carbon/react/icons';
import { Button, InlineLoading } from '@carbon/react';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import useClickOutside from 'lib/hooks/useClickOutside';
import {
  PANEL_OFFSET_PX,
  placePanelBelow,
  useAnchoredPanel,
} from 'lib/hooks/use-anchored-panel';
import { StatusItem } from 'pages/crm-tracts-subtracts/types';
import { StatusChecklist } from './status-checklist';
import style from './workflow-status-popover.module.scss';

interface WorkflowStatusPopoverProps {
  statusItems: StatusItem[];
  overallStatus: string;
  iconDescription?: string;
  ariaLabel?: string;
  dataTestId?: string;
  /** The trigger button's Carbon size, e.g. `sm` beside small controls (default `lg`). */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Render the checklist's panel rows — a status icon per row and labels that wrap — for long
   * step names (default false: the compact text-only rows).
   */
  showStatusIcon?: boolean;
  /**
   * The trigger's icon, for a caller that already shows the overall status beside it (default:
   * the overall status icon).
   */
  triggerIcon?: ReactNode;
  /**
   * Keep the panel inside the viewport vertically, for a trigger that can sit near the bottom of
   * the screen: open above the trigger when it does not fit below, and scroll inside it when it
   * fits neither way (default false: always open below).
   */
  fitViewportHeight?: boolean;
}

/**
 * Opens the panel below the trigger when it fits there, else above; when it fits neither way it
 * takes the roomier side and scrolls. Above ends at the app's fixed top bar, which draws over the
 * panel.
 * @param {HTMLDivElement} popover - The fixed-position panel
 * @param {DOMRect} rect - The trigger's rect
 */
const placeVertically = (popover: HTMLDivElement, rect: DOMRect) => {
  popover.style.maxHeight = '';
  popover.style.overflowY = '';
  const height = popover.offsetHeight;
  const viewportHeight = document.documentElement.clientHeight;
  const below = viewportHeight - rect.bottom - 2 * PANEL_OFFSET_PX;
  const topBar = document.querySelector('.bx--header, .cds--header');
  const ceiling = topBar ? topBar.getBoundingClientRect().bottom : 0;
  const above = rect.top - ceiling - 2 * PANEL_OFFSET_PX;
  if (height <= below) return;

  const room = Math.max(above, below);
  if (height > room) {
    popover.style.maxHeight = `${room}px`;
    popover.style.overflowY = 'auto';
  }
  if (above > below) {
    popover.style.top = `${
      rect.top - PANEL_OFFSET_PX - Math.min(height, room)
    }px`;
  }
};

export const WorkflowStatusPopover = ({
  statusItems,
  overallStatus,
  iconDescription = 'Show status',
  ariaLabel = 'Show status',
  dataTestId,
  size = 'lg',
  showStatusIcon = false,
  triggerIcon,
  fitViewportHeight = false,
}: WorkflowStatusPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const statusIcon = useMemo(() => {
    switch (overallStatus) {
      case 'processed':
        return <CheckmarkFilled className={style.statusIcon} />;
      case 'failed':
        return <ErrorFilled className={style.statusIconError} />;
      case 'processing':
      case 'pending':
      default:
        return (
          <InlineLoading status="active" className={style.statusIconPending} />
        );
    }
  }, [overallStatus]);

  // Below the trigger, pinned to its left edge and kept inside the viewport (placePanelBelow);
  // a consumer whose trigger can sit near the viewport bottom opts into fitViewportHeight.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;

    const button = triggerRef.current.querySelector('button');
    if (!button) return;

    const rect = placePanelBelow(button, popoverRef.current);
    if (fitViewportHeight) placeVertically(popoverRef.current, rect);
  }, [fitViewportHeight]);

  useAnchoredPanel(isOpen, updatePosition);

  // Watch button visibility and close popover when button becomes invisible
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const button = triggerRef.current.querySelector('button');
    if (!button) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) {
          setIsOpen(false);
        }
      },
      { threshold: 0 }
    );

    observer.observe(button);

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside({
    refs: [triggerRef, popoverRef],
    onClickOutside: handleClickOutside,
  });

  const toggleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const button = e.currentTarget as HTMLButtonElement;

    setIsOpen((prev) => {
      // If closing the popover (prev is true), blur the button
      if (prev && button) {
        button.blur();
      }
      return !prev;
    });
  }, []);
  const handlePopoverEvent = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <>
      <div ref={triggerRef} className={style.statusTriggerWrapper}>
        <Button
          hasIconOnly
          kind="ghost"
          size={size}
          onClick={toggleOpen}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          data-testid={dataTestId}
          className={style.statusButton}
          iconDescription={iconDescription}
        >
          {triggerIcon ?? statusIcon}
        </Button>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className={style.statusPopover}
            onClick={handlePopoverEvent}
            onMouseDown={handlePopoverEvent}
          >
            <StatusChecklist
              statusItems={statusItems}
              showStatusIcon={showStatusIcon}
            />
          </div>,
          document.body
        )}
    </>
  );
};
